import { API_ENDPOINTS } from '@common/constants/apiEndpoint';
import { SigninTorusParams } from '@common/types';
import { formatScreens } from '@common/utils';
import restClient from '@common/configs/api';
import io from './feathers.io';
import {
  FindCollectionsRequestParams,
  FindCollectionsResult,
  GetSharedAppResult,
} from 'click-types';

/**
 * Bootstrap reads (app + screens) go over REST instead of socket.io.
 *
 * These are public share GETs. On flaky mobile networks a socket.io connection
 * can fail to establish (e.g. WebSocket blocked by the carrier), which made
 * these calls queue and time out after 30s with NO server-side log. REST is a
 * plain stateless HTTPS request (gzip + CDN friendly) and is far more reliable
 * for the initial load. socket.io is still used for realtime data afterwards.
 */
const REST_TIMEOUT_MS = 45000;
const REST_RETRIES = 2;

const restGetWithRetry = async <T>(
  url: string,
  params?: Record<string, any>
): Promise<T> => {
  let lastError: unknown;
  for (let attempt = 0; attempt <= REST_RETRIES; attempt++) {
    try {
      const res = await restClient.get<T>(url, {
        params,
        timeout: REST_TIMEOUT_MS,
      });
      return res.data;
    } catch (error) {
      lastError = error;
      if (attempt < REST_RETRIES) {
        await new Promise((resolve) =>
          setTimeout(resolve, 2000 * (attempt + 1))
        );
      }
    }
  }
  throw lastError;
};

// App services
export const fetchApp = async (appId: string) => {
  const res = await restGetWithRetry<GetSharedAppResult>(
    `${API_ENDPOINTS.APP}/${appId}`
  );
  return { ...res, metadata: JSON.parse(res.metadata) };
};

export const fetchCollections = async (
  appId: string
): Promise<FindCollectionsResult | undefined> => {
  const findCollectionsRequestParams: FindCollectionsRequestParams = {
    appId,
  };

  const res: FindCollectionsResult = await io
    .service(API_ENDPOINTS.COLLECTIONS)
    .find({
      query: findCollectionsRequestParams,
    });

  return res;
};

// Page services
export const fetchPages = async (
  appId: string,
  params: Record<string, any>
) => {
  const res = await restGetWithRetry<{ data: any; expiresOn: any }>(
    `/v2/share/${appId}/screens`,
    {
      ...params,
      appId,
    }
  );

  return {
    data: formatScreens({ screens: res?.data }).list().toView(),
    expiresOn: res?.expiresOn,
  };
};

export const fetchTemplateById = async (
  appId: string,
  pageTemplateId: string
) => {
  try {
    const res = await io
      .service(API_ENDPOINTS.V3_PAGE_TEMPLATE)
      .get(pageTemplateId, {
        query: {
          appId,
        },
      });

    return res;
  } catch (e) {
    console.error(e);
  }
};

export const fetchBrandAssets = async (appId: string) => {
  try {
    const res = await io.service(API_ENDPOINTS.APP_STYLES).find({
      query: {
        appId,
      },
    });

    const brandAssetsMap: Record<string, any> = {};
    if (res.data) {
      res.data.forEach(
        (item: {
          id: string;
          value: any;
          screenType: any;
          category: string;
          type: string;
        }) => {
          let value = item.value;
          if (item.type === 'text') {
            //remove color from values
            delete value?.color;
          }

          if (!(item.category in brandAssetsMap)) {
            brandAssetsMap[item.category] = {
              [item.screenType]: {
                id: item.id,
                value,
              },
            };
          } else {
            brandAssetsMap[item.category] = {
              ...brandAssetsMap[item.category],
              [item.screenType]: { id: item.id, value },
            };
          }
        }
      );
    }

    return brandAssetsMap;
  } catch (e) {
    console.error(e);
  }
};

//TORUS
export const signinTorus = async (data: SigninTorusParams, appId: string) => {
  const { userPreview, accessToken } = await io.authenticate(
    { ...data, strategy: 'torus' },
    {
      query: {
        appId: appId,
      },
    }
  );

  return Object.assign(userPreview, { accessToken });
};

/**
 * Buckets whose objects are resolved through a pre-signed URL.
 *
 * File security used to be opt-in — gated behind a USE_FILE_SECURITY constant
 * and a per-field `fileSecurity` toggle on the canvas — and was documented as
 * being for platio-canvas only. It is now the default for every image, so both
 * switches are gone.
 *
 * `asteria-prod.platio-canvas.dbfile` was missing from this list, which meant
 * a stored full URL for the asteria stack fell through to the `startsWith('http')`
 * check in isS3Path and was never recognised. The one product the feature was
 * written for was the one it silently skipped.
 */
const S3_BUCKETS = [
  'asteria-prod.click-platform.dbfile',
  'asteria-prod.platio-canvas.dbfile',
  'dev.click-platform.dbfile',
  'staging.click-platform.dbfile',
];
/**
 * ex: "workspaces/ee5e3a29-f469-42f1-a455-fae3cb96ae4e/2026/04/17/user37187/circle-ci-icon-short.jpeg"
 * ex: "user37187/2026/04/17/circle-ci-icon-short.jpeg"
 * ex: "https://s3.ap-northeast-1.amazonaws.com/staging.click-platform.dbfile/user40221/2025/12/25/%E3%82%B9%E3%82%AF%E3%83%AA%E3%83%BC%E3%83%B3%E3%82%B7%E3%83%A7%E3%83%83%E3%83%88%202025-12-25%2013.50.09.png"
 * @param path
 * @returns pathObject ex: "workspaces/ee5e3a29-f469-42f1-a455-fae3cb96ae4e/2026/04/17/user37187/circle-ci-icon-short.jpeg"
 */
const getPathFromS3Url = (url: string): string => {
  const uri = url.split('?')[0];
  let path = uri.replace(/^https?:\/\/[^/]+\//, '');
  for (const bucket of S3_BUCKETS) {
    if (path.startsWith(`${bucket}/`)) {
      path = path.slice(bucket.length + 1);
      break;
    }
  }
  // The pathname segment in an S3 URL is percent-encoded (e.g. "β" → "%CE%B2").
  // Return the raw S3 key here — the HTTP layer re-encodes it when sent as the
  // `pathObject` query parameter. Without this decode, the key arrives at the
  // backend as the literal "%CE%B2…" and S3 signs a non-existent key.
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
};

const isS3Path = (path: string): boolean => {
  if (S3_BUCKETS.some((bucket) => path.includes(bucket))) {
    return true;
  }
  return !path.startsWith('http') && !path.startsWith('data');
};
/**
 * ex: "workspaces/ee5e3a29-f469-42f1-a455-fae3cb96ae4e/2026/04/17/user37187/circle-ci-icon-short.jpeg"
 * ex: "user37187/2026/04/17/circle-ci-icon-short.jpeg"
 * ex: "https://s3.ap-northeast-1.amazonaws.com/staging.click-platform.dbfile/user37187/2026/04/17/circle-ci-icon-short.jpeg"
 * @param path
 * @returns pathObject signed
 */
/**
 * The S3 key behind a stored reference, or null when it is not ours to sign -
 * an external URL, a data: URI.
 *
 * Exported so the builder can re-sign through its own authenticated `files`
 * service without copying `S3_BUCKETS`. That list has been wrong before:
 * `asteria-prod.platio-canvas.dbfile` was missing, so the one product the
 * feature was written for was the one it silently skipped. A second copy is
 * how that comes back.
 */
export const toS3KeyPath = (src: string): string | null =>
  isS3Path(src) ? getPathFromS3Url(src) : null;

/**
 * True only when a stored reference names one of our own S3 buckets.
 *
 * Deliberately narrower than `isS3Path`, which also treats *any* scheme-less
 * string as a bare S3 key. That is right where a bare key is the normal stored
 * shape, but wrong for the WebView element, whose `uri` may legitimately be a
 * scheme-less site address such as "example.com" - signing that would replace a
 * working page with a pre-signed URL for an object that does not exist.
 */
export const isClickS3Url = (src: string): boolean =>
  S3_BUCKETS.some((bucket) => src.includes(bucket));

export const fetchSecurityImage = async (path: string): Promise<string> => {
  const isS3 = isS3Path(path);
  if (!isS3) {
    return path;
  }
  const pathObject = getPathFromS3Url(path);
  const { url } = await io.service(API_ENDPOINTS.SHARE_FILES).find({
    query: {
      pathObject,
      expiresIn: 300, // 300 seconds = 5 minutes
    },
  });
  return url;
};

// Re-sign well before the URL actually dies, so a cached hit always has time
// left to finish a download that starts right after it is handed out.
const SIGNED_URL_REUSE_MS = 240 * 1000;

const signedUrlCache = new Map<string, { url: string; signedAt: number }>();
const signedUrlInFlight = new Map<string, Promise<string>>();

// An entry is dead weight once it is past reuse, but nothing goes back to
// collect it: a key signed once while scrolling is never asked for again, so
// without a bound the map grows for the lifetime of the session - one entry
// per distinct image, marker icon and audio file the app has ever shown.
const SIGNED_URL_CACHE_MAX = 500;

const pruneSignedUrlCache = () => {
  if (signedUrlCache.size <= SIGNED_URL_CACHE_MAX) {
    return;
  }
  const now = Date.now();
  signedUrlCache.forEach((entry, key) => {
    if (now - entry.signedAt > SIGNED_URL_REUSE_MS) {
      signedUrlCache.delete(key);
    }
  });
  // Still over after dropping the expired ones: give up the least recently
  // signed, which a Map yields in insertion order.
  const keys = signedUrlCache.keys();
  while (signedUrlCache.size > SIGNED_URL_CACHE_MAX) {
    const oldest = keys.next();
    if (oldest.done) break;
    signedUrlCache.delete(oldest.value);
  }
};

/** The still-reusable cached URL for a reference, or null. */
export const peekSignedImageUrl = (path: string): string | null => {
  const key = toS3KeyPath(path);
  if (key === null) {
    return path;
  }
  const hit = signedUrlCache.get(key);
  if (!hit || Date.now() - hit.signedAt > SIGNED_URL_REUSE_MS) {
    return null;
  }
  return hit.url;
};

/**
 * `fetchSecurityImage` with a per-key cache and in-flight de-duplication.
 *
 * A list signs one URL per item, and every re-render or remount signed again -
 * each new signature is a different URL, so the browser re-downloaded the same
 * bytes. Keyed on the S3 path, so two items showing the same image share one
 * round trip as well.
 *
 * Only the image hook uses this. The download and external-link actions keep
 * calling `fetchSecurityImage` directly: they fire once, at tap time, and want
 * the full five minutes rather than whatever is left of a cached signature.
 */
export const fetchSecurityImageCached = async (
  path: string
): Promise<string> => {
  const key = toS3KeyPath(path);
  if (key === null) {
    return path;
  }
  const cached = peekSignedImageUrl(path);
  if (cached) {
    return cached;
  }
  const pending = signedUrlInFlight.get(key);
  if (pending) {
    return pending;
  }
  const request = fetchSecurityImage(path)
    .then((url) => {
      // Re-insert rather than overwrite, so insertion order tracks when a key
      // was last signed and the prune above drops the genuinely coldest.
      signedUrlCache.delete(key);
      signedUrlCache.set(key, { url, signedAt: Date.now() });
      pruneSignedUrlCache();
      return url;
    })
    .finally(() => {
      signedUrlInFlight.delete(key);
    });
  signedUrlInFlight.set(key, request);
  return request;
};

/**
 * Sign an image reference that a record may store either as a bare URL or as
 * an upload object, handing back the shape it was given.
 *
 * Image fields are read in both shapes across the element set - the idiom is
 * `typeof value === 'object' ? value.url : value` - so a signer that took only
 * strings would have to be unwrapped and rewrapped at every call site.
 *
 * For surfaces that hand a URL to something other than React - a Google Maps
 * marker icon, a script injected into a WebView - where the `useImageSecurity`
 * hook has nothing to render.
 */
export const signImageRef = async (ref: any): Promise<any> => {
  if (typeof ref === 'string') {
    return ref ? fetchSecurityImageCached(ref) : ref;
  }
  const url = ref?.url;
  if (typeof url !== 'string' || !url) {
    return ref;
  }
  return { ...ref, url: await fetchSecurityImageCached(url) };
};

/**
 * Mint a fresh pre-signed URL for a stored file reference.
 *
 * Same signing path as `fetchSecurityImage`; named separately because the
 * download actions handle documents and archives, not only images, and reading
 * `fetchSecurityImage` there would misdescribe what is being fetched.
 */
export const fetchSignedFileUrl = fetchSecurityImage;
